import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { 
  Building2, 
  Plus, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  RefreshCw,
  Info,
  Crown,
  Lock,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Regímenes fiscales comunes
const REGIMENES_FISCALES = [
  { code: "601", name: "General de Ley Personas Morales" },
  { code: "603", name: "Personas Morales con Fines no Lucrativos" },
  { code: "605", name: "Sueldos y Salarios" },
  { code: "606", name: "Arrendamiento" },
  { code: "607", name: "Enajenación o Adquisición de Bienes" },
  { code: "608", name: "Demás ingresos" },
  { code: "610", name: "Residentes en el Extranjero" },
  { code: "612", name: "Personas Físicas con Actividades Empresariales" },
  { code: "620", name: "Sociedades Cooperativas de Producción" },
  { code: "621", name: "Incorporación Fiscal" },
  { code: "622", name: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { code: "625", name: "Actividades Empresariales a través de Plataformas" },
  { code: "626", name: "Régimen Simplificado de Confianza (RESICO)" }
];

const MultiRfcPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [rfcs, setRfcs] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // New RFC form
  const [newRfc, setNewRfc] = useState({
    rfc: "",
    razon_social: "",
    regimen_fiscal: "601",
    codigo_postal: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, rfcsRes] = await Promise.all([
        axios.get(`${API}/additional-rfc/status`, { headers }),
        axios.get(`${API}/additional-rfc`, { headers })
      ]);
      
      setStatus(statusRes.data);
      setRfcs(rfcsRes.data);
    } catch (error) {
      toast.error("Error al cargar información de Multi-RFC");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddRfc = async () => {
    if (!newRfc.rfc || !newRfc.razon_social || !newRfc.codigo_postal) {
      toast.error("Complete todos los campos requeridos");
      return;
    }
    
    // Basic RFC validation
    const rfcClean = newRfc.rfc.toUpperCase().trim();
    if (rfcClean.length < 12 || rfcClean.length > 13) {
      toast.error("El RFC debe tener 12 o 13 caracteres");
      return;
    }
    
    setAdding(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/additional-rfc`, {
        ...newRfc,
        rfc: rfcClean
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("RFC agregado exitosamente");
      setNewRfc({ rfc: "", razon_social: "", regimen_fiscal: "601", codigo_postal: "" });
      setAddDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar RFC");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteRfc = async (rfcId, rfcName) => {
    if (!window.confirm(`¿Está seguro de eliminar el RFC ${rfcName}?`)) {
      return;
    }
    
    setDeleting(rfcId);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.delete(`${API}/additional-rfc/${rfcId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("RFC eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar RFC");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  // If not enterprise plan, show upgrade message
  if (!status?.enabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Multi-RFC</h1>
          <p className="text-slate-500">Gestiona múltiples razones sociales desde una sola cuenta</p>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-800">Función Exclusiva del Plan Empresarial</h3>
                <p className="text-amber-700 mt-1">
                  Multi-RFC te permite manejar hasta 5 razones sociales diferentes desde una sola cuenta.
                  Ideal para grupos empresariales, contadores y negocios con múltiples entidades.
                </p>
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Hasta 5 RFCs adicionales
                  </p>
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Cada RFC con su propia razón social y régimen fiscal
                  </p>
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Comparten los certificados CSD/FIEL del RFC principal
                  </p>
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Factura a nombre de cualquier RFC registrado
                  </p>
                </div>
                <Button className="mt-4 bg-amber-600 hover:bg-amber-700" onClick={() => window.location.href = "/dashboard/subscription"}>
                  <Crown className="w-4 h-4 mr-2" />
                  Actualizar a Plan Empresarial
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tu RFC Principal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <Building2 className="w-10 h-10 text-[#5B9BD5]" />
              <div>
                <p className="font-mono text-lg font-semibold">{status?.main_rfc || "N/A"}</p>
                <p className="text-slate-600">{status?.main_razon_social || "Sin configurar"}</p>
              </div>
              <Badge className="ml-auto bg-[#5B9BD5]">Principal</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Multi-RFC</h1>
          <p className="text-slate-500">Gestiona múltiples razones sociales desde una sola cuenta</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-[#5B9BD5] hover:bg-[#4a8bc4]"
                disabled={status?.remaining === 0}
              >
                <Plus className="w-4 h-4" />
                Agregar RFC
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Agregar RFC Adicional</DialogTitle>
                <DialogDescription>
                  Ingresa los datos del nuevo RFC. Los certificados CSD/FIEL serán compartidos con el RFC principal.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="rfc">RFC *</Label>
                  <Input
                    id="rfc"
                    placeholder="XAXX010101000"
                    value={newRfc.rfc}
                    onChange={(e) => setNewRfc(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                    maxLength={13}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="razon_social">Razón Social *</Label>
                  <Input
                    id="razon_social"
                    placeholder="Empresa SA de CV"
                    value={newRfc.razon_social}
                    onChange={(e) => setNewRfc(prev => ({ ...prev, razon_social: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="regimen">Régimen Fiscal *</Label>
                  <Select
                    value={newRfc.regimen_fiscal}
                    onValueChange={(v) => setNewRfc(prev => ({ ...prev, regimen_fiscal: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona régimen" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMENES_FISCALES.map((reg) => (
                        <SelectItem key={reg.code} value={reg.code}>
                          {reg.code} - {reg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cp">Código Postal *</Label>
                  <Input
                    id="cp"
                    placeholder="06600"
                    value={newRfc.codigo_postal}
                    onChange={(e) => setNewRfc(prev => ({ ...prev, codigo_postal: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                    maxLength={5}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAddRfc} 
                  disabled={adding}
                  className="bg-[#5B9BD5] hover:bg-[#4a8bc4]"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Agregar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Status Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#5B9BD5]">{status?.current_count || 0}</p>
              <p className="text-sm text-slate-500">RFCs Adicionales</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{status?.remaining || 0}</p>
              <p className="text-sm text-slate-500">Disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">{status?.max_allowed || 5}</p>
              <p className="text-sm text-slate-500">Límite Máximo</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Crown className="w-8 h-8 text-amber-600 mx-auto" />
              <p className="text-sm font-medium text-amber-700 mt-1">Plan Empresarial</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main RFC */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#5B9BD5]" />
            RFC Principal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex-1">
              <p className="font-mono text-lg font-semibold text-blue-800">{status?.main_rfc || "N/A"}</p>
              <p className="text-blue-700">{status?.main_razon_social || "Sin configurar"}</p>
            </div>
            <Badge className="bg-blue-600">Principal</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Additional RFCs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            RFCs Adicionales
          </CardTitle>
          <CardDescription>
            Razones sociales adicionales que puedes usar para facturar
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rfcs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p>No tienes RFCs adicionales</p>
              <p className="text-sm">Haz clic en "Agregar RFC" para comenzar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rfcs.map((rfc) => (
                <div 
                  key={rfc.id} 
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border hover:border-slate-300 transition-colors"
                >
                  <Building2 className="w-8 h-8 text-slate-400" />
                  <div className="flex-1">
                    <p className="font-mono font-semibold">{rfc.rfc}</p>
                    <p className="text-sm text-slate-600">{rfc.razon_social}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        Régimen {rfc.regimen_fiscal}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        CP {rfc.codigo_postal}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteRfc(rfc.id, rfc.rfc)}
                    disabled={deleting === rfc.id}
                  >
                    {deleting === rfc.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Sobre Multi-RFC</AlertTitle>
        <AlertDescription>
          <ul className="list-disc ml-4 mt-2 space-y-1 text-sm">
            <li>Los RFCs adicionales comparten los certificados CSD/FIEL del RFC principal</li>
            <li>Al crear una factura, podrás seleccionar con cuál RFC emitirla</li>
            <li>Cada RFC puede tener su propio régimen fiscal y dirección</li>
            <li>El límite máximo es de 5 RFCs adicionales por cuenta</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default MultiRfcPage;
